import { Box, Typography, useMediaQuery, useTheme } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaInfoCircle } from 'react-icons/fa'
import { LuRefreshCcw } from 'react-icons/lu'
import { toast } from 'react-toastify'

import Accordion from '@/components/Accordion'
import DraggableContainer from '@/components/DraggableContainer'
import Icon from '@/components/Icon'
import Tooltip from '@/components/Tooltip'
import { useAppSelector } from '@/store'
import { DIGITAL_REGION } from '@/utils/constants'

import ColorCheckerContainer from './ColorCheckerContainer'

// const COLOR_CHECKER_WIDTH = 1255

interface ColorCheckerProps {
  dimensions: { width: number; height: number }
  isVerticallyAligned: boolean
  reset: () => void
  containerRef?: React.RefObject<HTMLDivElement>
  setForceActive?: (activate: boolean) => void
}

const ColorChecker: React.FC<ColorCheckerProps> = ({
  dimensions,
  isVerticallyAligned,
  reset,
  containerRef,
  setForceActive,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const handleTooltipClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
  }

  const isAbove1920 = useMediaQuery('(min-width:1920px)')
  const isAbove1440 = useMediaQuery('(min-width:1440px)')
  const isAbove1024 = useMediaQuery('(min-width:1024px)')

  const COLOR_CHECKER_WIDTH = useMemo(() => {
    if (isAbove1920) {
      return 1255
    }
    if (isAbove1440) {
      return 943
    }
    if (isAbove1024) {
      return 703
    }
    return 703
  }, [isAbove1920, isAbove1440, isAbove1024])

  const dualCanvasImage = useAppSelector((state) => state.app.dualCanvasImage)

  const isDisabled =
    dualCanvasImage?.left?.type !== dualCanvasImage?.right?.type

  const [expanded, setExpanded] = useState(!isDisabled)

  useEffect(() => {
    setExpanded(!isDisabled)
  }, [isDisabled])

  const handleAccordionChange = (
    _: React.SyntheticEvent,
    newExpanded: boolean,
  ) => {
    if (isDisabled) {
      toast.error(t('digitalTryIn.imageTypeNotSame'))
    } else {
      setExpanded(newExpanded)
    }
  }

  const title = (
    <Box display="flex" gap={0.5}>
      <Typography sx={{ fontSize: '18px', fontWeight: 400 }}>
        {t('digitalTryIn.colorChecker')}
      </Typography>
      <Box>
        <Tooltip
          text={t('digitalTryIn.selectInfo')}
          onClick={handleTooltipClick}
          icon={<Icon icon={FaInfoCircle} />}
        />
        <Tooltip
          onClick={handleTooltipClick}
          text={t('digitalTryIn.refreshInfo')}
          icon={<Icon icon={LuRefreshCcw} onClick={reset} />}
        />
      </Box>
    </Box>
  )

  return (
    <DraggableContainer
      axis="x"
      bounds="parent"
      defaultPosition={{
        x:
          (dimensions.width - COLOR_CHECKER_WIDTH / 2) /
          (isVerticallyAligned ? 2 : 1),
        y: dimensions.height * (isVerticallyAligned ? 2 : 1),
      }}
    >
      <Box>
        <Accordion
          title={title}
          sx={{
            background: theme.palette.common.black,
            width: COLOR_CHECKER_WIDTH,
            transform: 'translate(0%, -100%)',
            '&.MuiPaper-root': {
              borderBottomLeftRadius: 0,
              borderBottomRightRadius: 0,
            },
          }}
          accordionSummarySx={{
            padding: '10px 16px',
            maxHeight: '44px',
          }}
          accordionDetailsSx={{
            padding: '24px 32px 32px 32px',
            maxHeight: isAbove1920 ? '146px' : 'none',
          }}
          titleSx={{ fontSize: '20px' }}
          controlProps={{
            expanded,
            onChange: handleAccordionChange,
          }}
        >
          <Box display="flex" flexDirection="row" flexGrow={1} gap={3}>
            {Object.values(DIGITAL_REGION).map((region, index, arr) => (
              <Box
                display="flex"
                flexDirection="column"
                flexGrow={1}
                key={region}
                sx={{
                  borderRight:
                    index !== arr.length - 1
                      ? `2px solid rgba(128, 128, 128, 0.55)`
                      : 'none',
                  pr: 2,
                }}
              >
                <Box
                  lineHeight="normal"
                  fontSize="16px"
                  mb={2}
                  fontWeight={400}
                >
                  {t(`region.${region}`)}
                </Box>
                <ColorCheckerContainer
                  region={region}
                  containerRef={containerRef}
                  setForceActive={setForceActive}
                />
              </Box>
            ))}
          </Box>
        </Accordion>
      </Box>
    </DraggableContainer>
  )
}

export default ColorChecker
